import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import ProfileDropdown from '../components/ProfileDropdown'
import { 
  CameraIcon, 
  UserGroupIcon, 
  ClipboardDocumentListIcon, 
  DocumentTextIcon,
  CheckIcon,
  PlayIcon,
  ArrowRightIcon,
  StarIcon,
  BoltIcon,
  BuildingOfficeIcon,
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  LightBulbIcon,
  CalendarIcon
} from '@heroicons/react/24/outline'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWandSparkles } from '@fortawesome/free-solid-svg-icons'
import CountUpNumber from '../components/CountUpNumber'
import CurrencySelector from '../components/CurrencySelector'
import { formatPrice, getCurrencyForRegion } from '../utils/currency'

const LandingPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  
  // Get currency based on region selection
  const currentCurrency = getCurrencyForRegion()

  // Close mobile menu when clicking on a link
  const closeMobileMenu = () => setIsMobileMenuOpen(false)

  // Check if user is logged in (regardless of remember me setting)
  const isLoggedIn = !!user

  const features = [
    {
      icon: CameraIcon,
      title: 'Shoot Management',
      description: 'Organize your photo and video shoots with intelligent scheduling, location management, and real-time collaboration tools.',
      color: 'bg-brand-500/20 text-brand-400 dark:bg-brand-500/10 dark:text-brand-300',
      glowColor: 'group-hover:shadow-brand-500/20'
    },
    {
      icon: UserGroupIcon,
      title: 'Client Portal & Database',
      description: 'Centralized client management with secure portals for file sharing, feedback, and seamless communication.',
      color: 'bg-brand-700/20 text-brand-600 dark:bg-brand-700/10 dark:text-brand-500',
      glowColor: 'group-hover:shadow-brand-700/20'
    },
    {
      icon: ClipboardDocumentListIcon,
      title: 'Smart Task Lists',
      description: 'AI-powered task management that adapts to your workflow, with automated reminders and priority sorting.',
      color: 'bg-emerald-500/20 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300',
      glowColor: 'group-hover:shadow-emerald-500/20'
    },
    {
      icon: DocumentTextIcon,
      title: 'Professional Invoicing',
      description: 'Create and send professional invoices with integrated payment processing and automated follow-ups.',
      color: 'bg-orange-500/20 text-orange-600 dark:bg-orange-500/10 dark:text-orange-300',
      glowColor: 'group-hover:shadow-orange-500/20'
    }
  ]

  const benefits = [
    { text: 'Save 15+ hours per week on admin tasks', icon: BoltIcon },
    { text: 'Increase client satisfaction by 40%', icon: StarIcon },
    { text: 'Never miss a deadline or payment', icon: CheckIcon },
    { text: 'Scale your freelance business efficiently', icon: ArrowRightIcon }
  ]

  const stats = [
    { number: '10K+', label: 'Active Users' },
    { number: '50K+', label: 'Projects Managed' },
    { number: '99.9%', label: 'Uptime' },
    { number: '24/7', label: 'Support' }
  ]

  const pricingPlans = [
    {
      name: 'Freemium',
      icon: UserIcon,
      price: 'Free',
      period: 'forever',
      description: 'Perfect for getting started with basic shoot management',
      features: [
        'Up to 5 client projects',
        'Basic shoot scheduling',
        'Simple task management',
        'Email support',
        'Basic invoicing (5/month)',
        'Client portal access'
      ],
      buttonText: 'Get Started Free',
      popular: false,
      gradient: 'from-emerald-600 to-emerald-500 dark:from-emerald-500 dark:to-emerald-400'
    },
    {
      name: 'Professional',
      icon: CameraIcon,
      price: `${currentCurrency.symbol}${formatPrice('professional', currentCurrency.code)}`,
      period: '/month',
      description: 'Ideal for photographers and videographers',
      features: [
        'Unlimited client projects',
        'Advanced shoot management',
        'AI-powered task automation',
        'Priority support',
        'Unlimited invoicing',
        'Advanced client portals',
        'File storage (100GB)',
        'Custom branding',
        'Analytics & reports'
      ],
      buttonText: 'Get Started',
      popular: true,
      gradient: 'from-blue-600 to-purple-600 dark:from-brand-800 dark:to-brand-500'
    },
    {
      name: 'Studio',
      icon: BuildingOfficeIcon,
      price: `${currentCurrency.symbol}${formatPrice('studio', currentCurrency.code)}`,
      period: '/month',
      description: 'Built for creative studios and growing teams',
      features: [
        'Everything in Professional',
        'Team collaboration tools',
        'Multi-user access (up to 10)',
        'Advanced project templates',
        'White-label client portals',
        'File storage (1TB)',
        'Advanced analytics',
        'API access',
        'Dedicated account manager'
      ],
      buttonText: 'Contact Sales',
      popular: false,
      gradient: 'from-purple-600 to-pink-600 dark:from-brand-700 dark:to-purple-500'
    }
  ]

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>



      {/* Header */}
      <header className="relative z-20 glass-header">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 lg:py-6">
            <div className="flex items-center space-x-3">
              <motion.button 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 lg:space-x-3 hover:opacity-80 transition-opacity"
              >
                <div className="w-10 h-10 lg:w-12 lg:h-12 glass-badge rounded-2xl flex items-center justify-center">
                  <CameraIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                </div>
                <span className="text-xl lg:text-2xl font-bold text-white">
                  Shoot<span className="text-gray-400">Suite</span>
                </span>
              </motion.button>
              
              {/* Beta Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="relative"
              >
                <div className="relative">
                  {/* Gradient border effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full blur-sm opacity-60 animate-pulse"></div>
                  <div className="relative glass-badge rounded-full px-3 py-1 border-2 border-transparent bg-gradient-to-r from-emerald-500/20 to-teal-500/20 backdrop-blur-sm">
                    {/* Gradient border overlay */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 opacity-50"></div>
                    <div className="absolute inset-[2px] rounded-full bg-black/80 backdrop-blur-sm"></div>
                    <div className="relative text-xs font-semibold text-white">
                      Now In Beta
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* Desktop Navigation */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="hidden md:flex items-center space-x-6 lg:space-x-8"
            >
              <a href="#features" className="text-gray-400 hover:text-white transition-colors font-medium text-sm lg:text-base">
                {t('nav.features', 'Features')}
              </a>
              <a href="#pricing" className="text-gray-400 hover:text-white transition-colors font-medium text-sm lg:text-base">
                {t('nav.pricing', 'Pricing')}
              </a>
              <Link to="/roadmap" className="text-gray-400 hover:text-white transition-colors font-medium text-sm lg:text-base">
                Roadmap
              </Link>
              <Link to="/updates" className="text-gray-400 hover:text-white transition-colors font-medium text-sm lg:text-base">
                Updates
              </Link>
              
              {isLoggedIn ? (
                // Logged in user navigation
                <>
                  <Link to="/dashboard" className="btn-gradient text-sm lg:text-base py-2 lg:py-4 px-4 lg:px-8">
                    Dashboard
                  </Link>
                  <ProfileDropdown compact />
                </>
              ) : (
                // Default navigation for non-logged in users
                <>
                  <Link to="/auth" className="btn-secondary text-sm lg:text-base py-2 lg:py-4 px-4 lg:px-8">
                    {t('nav.signIn', 'Sign In')}
                  </Link>
                  <Link to="/auth" className="btn-gradient text-sm lg:text-base py-2 lg:py-4 px-4 lg:px-8">
                    {t('nav.getStarted', 'Get Started')}
                  </Link>
                </>
              )}
            </motion.div>

            {/* Mobile Menu Button */}
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-white hover:text-gray-300 transition-colors"
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </motion.button>
          </div>

          {/* Mobile Navigation Menu */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="md:hidden overflow-hidden"
              >
                <div className="glass-mockup rounded-2xl mx-4 mb-4 p-6 space-y-4">
                  <a 
                    href="#features" 
                    onClick={closeMobileMenu}
                    className="block text-gray-300 hover:text-white transition-colors font-medium py-2"
                  >
                    {t('nav.features', 'Features')}
                  </a>
                  <a 
                    href="#pricing" 
                    onClick={closeMobileMenu}
                    className="block text-gray-300 hover:text-white transition-colors font-medium py-2"
                  >
                    {t('nav.pricing', 'Pricing')}
                  </a>
                  <Link 
                    to="/roadmap" 
                    onClick={closeMobileMenu}
                    className="block text-gray-300 hover:text-white transition-colors font-medium py-2"
                  >
                    Roadmap
                  </Link>
                  <Link 
                    to="/updates" 
                    onClick={closeMobileMenu}
                    className="block text-gray-300 hover:text-white transition-colors font-medium py-2"
                  >
                    Updates
                  </Link>
                  <div className="pt-4 space-y-3">
                    {isLoggedIn ? (
                      // Logged in user mobile navigation
                      <>
                        <Link 
                          to="/dashboard" 
                          onClick={closeMobileMenu}
                          className="block w-full text-center btn-gradient py-3"
                        >
                          Dashboard
                        </Link>
                        <div className="flex justify-center pt-2">
                          <ProfileDropdown compact />
                        </div>
                      </>
                    ) : (
                      // Default mobile navigation for non-logged in users
                      <>
                        <Link 
                          to="/auth" 
                          onClick={closeMobileMenu}
                          className="block w-full text-center btn-secondary py-3"
                        >
                          {t('nav.signIn', 'Sign In')}
                        </Link>
                        <Link 
                          to="/auth" 
                          onClick={closeMobileMenu}
                          className="block w-full text-center btn-gradient py-3"
                        >
                          {t('nav.getStarted', 'Get Started')}
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-12 sm:py-16 lg:py-24 xl:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:grid lg:grid-cols-12 lg:gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="col-span-5 text-center lg:text-left"
            >
              <motion.div 
                className="mb-6 lg:mb-8"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div className="relative inline-flex px-3 py-2 lg:px-4 lg:py-2 rounded-full items-center space-x-2 bg-white/10 backdrop-blur-20 border border-white/20">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 animate-pulse"></div>
                  <div className="w-4 h-4 lg:w-5 lg:h-5 relative z-10 flex items-center justify-center">
                    <FontAwesomeIcon icon={faWandSparkles} className="text-blue-400 text-sm lg:text-base" />
                  </div>
                  <span className="text-gray-300 font-medium relative z-10 text-sm lg:text-base">
                    {t('hero.badge', 'For Creative Professionals')}
                  </span>
                </div>
              </motion.div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-7xl font-bold leading-tight tracking-tight mb-6 lg:mb-8 text-white">
                {t('hero.title', 'Streamline Your')} 
                <span className="text-gradient block mt-1 lg:mt-2">
                  {t('hero.titleHighlight', 'Creative Business')}
                </span>
              </h1>
              
              <p className="text-lg lg:text-xl leading-relaxed text-gray-400 mb-8 lg:mb-12 max-w-2xl mx-auto lg:mx-0">
                {t('hero.description', 'The all-in-one productivity platform designed to streamline photography and videography workflows. Manage shoots, clients, tasks, invoices, and more with ease.')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 lg:gap-6 mb-8 lg:mb-12">
                <Link to="/auth" className="w-full sm:w-auto">
                  <motion.button 
                    className="btn-primary flex items-center justify-center space-x-3 text-base lg:text-lg w-full py-4 lg:py-4 px-6 lg:px-8"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>{t('hero.getStarted', 'Get Started')}</span>
                    <ArrowRightIcon className="w-5 h-5" />
                  </motion.button>
                </Link>
                <motion.button 
                  className="btn-secondary flex items-center justify-center space-x-3 text-base lg:text-lg w-full sm:w-auto py-4 lg:py-4 px-6 lg:px-8"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <PlayIcon className="w-5 h-5" />
                  <span>{t('hero.watchDemo', 'Watch Demo')}</span>
                </motion.button>
              </div>
              
              <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6 text-sm text-gray-400">
                <div className="flex items-center justify-center lg:justify-start space-x-2">
                  <CheckIcon className="w-4 h-4 gradient-icon flex-shrink-0" />
                  <span className="text-center lg:text-left">{t('hero.benefits.aiPowered', 'AI-powered workflow automation')}</span>
                </div>
                <div className="flex items-center justify-center lg:justify-start space-x-2">
                  <CheckIcon className="w-4 h-4 gradient-icon flex-shrink-0" />
                  <span className="text-center lg:text-left">{t('hero.benefits.realTimeSync', 'Real-time client collaboration')}</span>
                </div>
                <div className="flex items-center justify-center lg:justify-start space-x-2">
                  <CheckIcon className="w-4 h-4 gradient-icon flex-shrink-0" />
                  <span className="text-center lg:text-left">{t('hero.benefits.smartScheduling', 'Smart scheduling & conflict detection')}</span>
                </div>
              </div>
            </motion.div>
            
            {/* Dashboard Preview - Hidden on mobile, simplified on tablet */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block col-span-7 mt-16 lg:mt-0"
            >
              <div className="relative">
                <motion.div 
                  className="relative animate-float"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl transform rotate-1 blur-xl"></div>
                  <div className="relative glass-mockup rounded-2xl overflow-hidden max-h-[600px]">
                    {/* macOS Window Header */}
                    <div className="glass-badge px-4 py-3 flex items-center space-x-2 border-b border-white/20">
                      <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                      <div className="ml-3 text-gray-300 text-sm font-medium">ShootSuite Dashboard</div>
                    </div>
                    
                    {/* Compact Dashboard Content */}
                    <div className="p-6 bg-gradient-to-br from-black/40 to-black/60 overflow-hidden">
                      {/* Welcome Header */}
                      <div className="mb-6">
                        <motion.h1 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                          className="text-2xl font-bold text-white mb-2"
                        >
                          Welcome back, <span className="text-gradient">Lens</span>!
                        </motion.h1>
                        <motion.p 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                          className="text-gray-400 text-sm"
                        >
                          Here's what's happening with your creative business today.
                        </motion.p>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-4 gap-3 mb-6">
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5 }}
                          className="glass-mockup rounded-xl p-3 hover:scale-105 transition-transform"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <CameraIcon className="w-5 h-5 text-gray-300" />
                            <span className="text-xl font-bold text-white">12</span>
                          </div>
                          <p className="text-gray-400 text-xs font-medium">Active Projects</p>
                        </motion.div>
                        
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.6 }}
                          className="glass-mockup rounded-xl p-3 hover:scale-105 transition-transform"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <DocumentTextIcon className="w-5 h-5 text-gray-300" />
                            <span className="text-xl font-bold text-white">$8,450</span>
                          </div>
                          <p className="text-gray-400 text-xs font-medium">This Month Revenue</p>
                        </motion.div>

                        <motion.div 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.7 }}
                          className="glass-mockup rounded-xl p-3 hover:scale-105 transition-transform"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <CameraIcon className="w-5 h-5 text-gray-300" />
                            <span className="text-xl font-bold text-white">5</span>
                          </div>
                          <p className="text-gray-400 text-xs font-medium">Upcoming Shoots</p>
                        </motion.div>

                        <motion.div 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.8 }}
                          className="glass-mockup rounded-xl p-3 hover:scale-105 transition-transform"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <ClipboardDocumentListIcon className="w-5 h-5 text-gray-300" />
                            <span className="text-xl font-bold text-white">8</span>
                          </div>
                          <p className="text-gray-400 text-xs font-medium">Pending Tasks</p>
                        </motion.div>
                      </div>

                      {/* Quick Actions */}
                      <div className="mb-6">
                        <motion.h2 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.9 }}
                          className="text-lg font-bold text-white mb-4"
                        >
                          Quick Actions
                        </motion.h2>
                        <div className="grid grid-cols-4 gap-3">
                          <motion.button 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.0 }}
                            className="glass-mockup rounded-xl p-4 text-left hover:scale-105 transition-all group"
                          >
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                              <CameraIcon className="w-4 h-4 text-white" />
                            </div>
                            <h3 className="text-white font-semibold text-sm mb-1">New Shoot</h3>
                            <p className="text-gray-400 text-xs">Schedule a new photo or video shoot</p>
                            <div className="flex items-center mt-2 text-blue-400 group-hover:text-blue-300 transition-colors">
                              <span className="text-xs font-medium">Get started</span>
                              <ArrowRightIcon className="w-3 h-3 ml-1" />
                            </div>
                          </motion.button>
                          
                          <motion.button 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.1 }}
                            className="glass-mockup rounded-xl p-4 text-left hover:scale-105 transition-all group"
                          >
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                              <UserGroupIcon className="w-4 h-4 text-white" />
                            </div>
                            <h3 className="text-white font-semibold text-sm mb-1">Add Client</h3>
                            <p className="text-gray-400 text-xs">Create a new client profile</p>
                            <div className="flex items-center mt-2 text-blue-400 group-hover:text-blue-300 transition-colors">
                              <span className="text-xs font-medium">Get started</span>
                              <ArrowRightIcon className="w-3 h-3 ml-1" />
                            </div>
                          </motion.button>

                          <motion.button 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.2 }}
                            className="glass-mockup rounded-xl p-4 text-left hover:scale-105 transition-all group"
                          >
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-orange-500 to-red-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                              <DocumentTextIcon className="w-4 h-4 text-white" />
                            </div>
                            <h3 className="text-white font-semibold text-sm mb-1">Create Invoice</h3>
                            <p className="text-gray-400 text-xs">Generate a new invoice</p>
                            <div className="flex items-center mt-2 text-blue-400 group-hover:text-blue-300 transition-colors">
                              <span className="text-xs font-medium">Get started</span>
                              <ArrowRightIcon className="w-3 h-3 ml-1" />
                            </div>
                          </motion.button>

                          <motion.button 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.3 }}
                            className="glass-mockup rounded-xl p-4 text-left hover:scale-105 transition-all group"
                          >
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-purple-500 to-pink-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                              <ClipboardDocumentListIcon className="w-4 h-4 text-white" />
                            </div>
                            <h3 className="text-white font-semibold text-sm mb-1">Task List</h3>
                            <p className="text-gray-400 text-xs">Manage your tasks and deadlines</p>
                            <div className="flex items-center mt-2 text-blue-400 group-hover:text-blue-300 transition-colors">
                              <span className="text-xs font-medium">Get started</span>
                              <ArrowRightIcon className="w-3 h-3 ml-1" />
                            </div>
                          </motion.button>
                        </div>
                      </div>

                      {/* Recent Activity & Upcoming Schedule */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* Activity Feed */}
                        <div className="glass-mockup rounded-xl p-4">
                          <motion.h3 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.4 }}
                            className="text-sm font-bold text-white mb-3"
                          >
                            Recent Activity
                          </motion.h3>
                          <div className="space-y-2">
                            <motion.div 
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 1.5 }}
                              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-white/5 transition-colors"
                            >
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <div className="flex-1">
                                <p className="text-white text-xs font-medium">New shoot scheduled</p>
                                <p className="text-gray-400 text-xs">Sarah Johnson</p>
                              </div>
                              <span className="text-gray-500 text-xs">2h ago</span>
                            </motion.div>
                            
                            <motion.div 
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 1.6 }}
                              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-white/5 transition-colors"
                            >
                              <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                              <div className="flex-1">
                                <p className="text-white text-xs font-medium">Invoice sent</p>
                                <p className="text-gray-400 text-xs">Tech Startup Co.</p>
                              </div>
                              <span className="text-gray-500 text-xs">4h ago</span>
                            </motion.div>
                          </div>
                        </div>

                        {/* Upcoming Schedule */}
                        <div className="glass-mockup rounded-xl p-4">
                          <motion.h3 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.7 }}
                            className="text-sm font-bold text-white mb-3"
                          >
                            Upcoming Schedule
                          </motion.h3>
                          <div className="space-y-2">
                            <motion.div 
                              initial={{ opacity: 0, x: 10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 1.8 }}
                              className="p-3 rounded-lg bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <h4 className="text-white font-semibold text-xs">Wedding Shoot</h4>
                                <span className="text-blue-400 text-xs">Tomorrow</span>
                              </div>
                              <p className="text-gray-400 text-xs">Sarah & Mike Johnson</p>
                              <p className="text-gray-500 text-xs">2:00 PM - 6:00 PM</p>
                            </motion.div>
                            
                            <motion.div 
                              initial={{ opacity: 0, x: 10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 1.9 }}
                              className="p-3 rounded-lg bg-white/5 border border-white/10"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <h4 className="text-white font-semibold text-xs">Product Photography</h4>
                                <span className="text-gray-400 text-xs">Friday</span>
                              </div>
                              <p className="text-gray-400 text-xs">Tech Startup Co.</p>
                              <p className="text-gray-500 text-xs">10:00 AM - 2:00 PM</p>
                            </motion.div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 lg:py-24 relative">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Glassmorphism background */}
            <div className="absolute inset-0 bg-white/5 backdrop-blur-20 rounded-3xl border border-white/10"></div>
            <div className="relative grid grid-cols-2 md:grid-cols-4 gap-12 p-12">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-4xl lg:text-5xl font-bold text-white mb-3">
                    <CountUpNumber end={stat.number} />
                  </div>
                  <div className="text-gray-400 font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 lg:py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 lg:mb-20"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4 lg:mb-6 text-white">
              Everything You Need to <span className="text-gradient">Succeed</span>
            </h2>
            <p className="text-lg lg:text-xl leading-relaxed text-gray-400 max-w-3xl mx-auto">
              Built specifically for creative professionals who demand efficiency without compromising quality.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="glass-mockup rounded-2xl p-6 text-left hover:scale-105 transition-all duration-300 h-full">
                  {/* Icon with gradient background */}
                  <div className="w-12 h-12 rounded-xl mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                       style={{
                         background: index === 0 ? 'linear-gradient(135deg, #3b82f6, #8b5cf6)' :
                                   index === 1 ? 'linear-gradient(135deg, #10b981, #06b6d4)' :
                                   index === 2 ? 'linear-gradient(135deg, #f59e0b, #ef4444)' :
                                   'linear-gradient(135deg, #8b5cf6, #ec4899)'
                       }}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-lg font-semibold mb-3 text-white group-hover:text-gray-100 transition-colors">
                    {feature.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-gray-400 text-sm leading-relaxed mb-4 group-hover:text-gray-300 transition-colors">
                    {feature.description}
                  </p>
                  
                  {/* Get Started Link */}
                  <div className="flex items-center text-blue-400 group-hover:text-blue-300 transition-colors">
                    <span className="text-sm font-medium">Get started</span>
                    <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* And So Much More Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="mt-16 max-w-4xl mx-auto"
          >
            <div className="glass-mockup rounded-2xl p-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7, type: "spring", stiffness: 200 }}
                className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)' }}
              >
                <FontAwesomeIcon icon={faWandSparkles} className="w-7 h-7 text-white" />
              </motion.div>
              
              <h3 className="text-2xl font-bold text-white mb-4">
                And <span className="text-gradient">So Much More</span>
              </h3>
              
              <p className="text-gray-400 mb-6 text-base leading-relaxed max-w-2xl mx-auto">
                These core features are just the beginning. ShootSuite includes 50+ tools and capabilities designed to streamline every aspect of your creative business.
              </p>
              
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8 }}
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm"
              >
                {[
                  'AI Automation',
                  'Advanced Analytics', 
                  'Custom Workflows',
                  'API Integrations',
                  'Mobile Apps',
                  'Team Collaboration'
                ].map((feature, index) => (
                  <motion.span
                    key={feature}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.9 + index * 0.1 }}
                    className="px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-gray-300 hover:bg-white/20 hover:text-white transition-all cursor-default"
                  >
                    {feature}
                  </motion.span>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 lg:py-24 relative overflow-hidden">
        {/* Background overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-purple-900/20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            {/* Story Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center lg:text-left"
            >
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="inline-flex items-center space-x-2 glass-badge rounded-full px-4 py-2 mb-6"
              >
                <CameraIcon className="w-5 h-5 text-blue-400" />
                <span className="text-white font-medium">Our Story</span>
              </motion.div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-white mb-6 lg:mb-8">
                Born from <span className="text-gradient">Real Struggle</span>
              </h2>
              
              <div className="space-y-6 text-lg lg:text-xl leading-relaxed text-gray-300">
                <p>
                  When I started as a freelance photographer and videographer, I quickly realized the biggest challenge wasn't capturing the perfect shot—it was managing everything else.
                </p>
                
                <p>
                  I found myself juggling <strong className="text-white">three separate software tools</strong> just to run my business: one for scheduling shoots, another for client management, and a third for invoicing. The constant switching between platforms was eating away at my creative time and causing me to miss important deadlines.
                </p>
                
                <p>
                  That's when I knew there had to be a better way. I envisioned a single dashboard where I could handle everything—from initial client contact to final payment—without the chaos of multiple tools.
                </p>
                
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 my-8"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <LightBulbIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-2">Our Mission</h3>
                      <p className="text-gray-300 text-base">
                        To help newer freelancers avoid the workflow chaos I experienced, while streamlining operations for established professionals who want to scale efficiently.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
            
            {/* Visual Elements */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="mt-16 lg:mt-0"
            >
              <div className="relative">
                {/* Before vs After Workflow */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="space-y-8"
                >
                  {/* Before - Multiple Tools */}
                  <div className="glass-mockup rounded-2xl p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                      <h3 className="text-white font-semibold">Before: The Chaos</h3>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 text-center">
                        <CalendarIcon className="w-6 h-6 text-red-400 mx-auto mb-2" />
                        <p className="text-white text-sm font-medium">Tool A</p>
                        <p className="text-gray-400 text-xs">Scheduling</p>
                      </div>
                      <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 text-center">
                        <UserGroupIcon className="w-6 h-6 text-red-400 mx-auto mb-2" />
                        <p className="text-white text-sm font-medium">Tool B</p>
                        <p className="text-gray-400 text-xs">Clients</p>
                      </div>
                      <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 text-center">
                        <DocumentTextIcon className="w-6 h-6 text-red-400 mx-auto mb-2" />
                        <p className="text-white text-sm font-medium">Tool C</p>
                        <p className="text-gray-400 text-xs">Invoicing</p>
                      </div>
                    </div>
                    <div className="mt-4 text-center">
                      <p className="text-red-400 text-sm">Constant switching • Lost time • Missed opportunities</p>
                    </div>
                  </div>

                  {/* Arrow */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                    className="flex justify-center"
                  >
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                      <ArrowRightIcon className="w-6 h-6 text-white transform rotate-90 lg:rotate-0" />
                    </div>
                  </motion.div>

                  {/* After - ShootSuite */}
                  <div className="glass-mockup rounded-2xl p-6 border-emerald-500/30">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-3 h-3 bg-emerald-400 rounded-full"></div>
                      <h3 className="text-white font-semibold">After: ShootSuite</h3>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-6 text-center">
                      <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <CameraIcon className="w-8 h-8 text-white" />
                      </div>
                      <p className="text-white font-bold text-lg mb-2">One Unified Dashboard</p>
                      <div className="flex justify-center space-x-4 mb-4">
                        <CalendarIcon className="w-5 h-5 text-emerald-400" />
                        <UserGroupIcon className="w-5 h-5 text-emerald-400" />
                        <DocumentTextIcon className="w-5 h-5 text-emerald-400" />
                        <ClipboardDocumentListIcon className="w-5 h-5 text-emerald-400" />
                      </div>
                      <p className="text-gray-300 text-sm">Shoots • Clients • Invoices • Tasks • Analytics</p>
                    </div>
                    <div className="mt-4 text-center">
                      <p className="text-emerald-400 text-sm">Streamlined workflow • More creative time • Growing business</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 lg:py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 lg:mb-20"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4 lg:mb-6 text-white">
              Choose Your <span className="text-gradient">Perfect Plan</span>
            </h2>
            <p className="text-lg lg:text-xl leading-relaxed text-gray-400 max-w-3xl mx-auto">
              Start free and scale as your creative business grows. No hidden fees, cancel anytime.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative transition-all duration-300 group ${
                  plan.popular 
                    ? 'gradient-border transform scale-105' 
                    : 'card hover:border-gray-300'
                }`}
                whileHover={{ y: -5 }}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="glass-badge text-white px-4 py-2 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl glass-badge flex items-center justify-center group-hover:scale-110 transition-transform">
                    <plan.icon className="w-8 h-8 text-gray-300" />
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-3 text-white">{plan.name}</h3>
                  <p className="text-gray-400 mb-8">{plan.description}</p>
                  
                  <div className="mb-8">
                    <span className="text-5xl font-bold text-white">{plan.price}</span>
                    <span className="text-gray-400 ml-2">{plan.period}</span>
                  </div>
                  
                  <Link to="/auth" className="block w-full mb-8">
                    <motion.button 
                      className={`w-full ${
                        plan.popular
                          ? 'btn-primary'
                          : 'btn-secondary'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {plan.buttonText}
                    </motion.button>
                  </Link>
                </div>
                
                <div className="space-y-4">
                  {plan.features.map((feature, featureIndex) => (
                    <motion.div
                      key={featureIndex}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: (index * 0.1) + (featureIndex * 0.05) }}
                      className="flex items-center space-x-3"
                    >
                      <CheckIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-300">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-gray-400 mb-4">
              Need a custom solution for your enterprise?
            </p>
            <button className="text-blue-400 font-medium hover:underline">
              Contact us for enterprise pricing
            </button>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 lg:py-24 relative overflow-hidden">
        {/* Dark overlay for contrast */}
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:grid lg:grid-cols-2 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center lg:text-left"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-white mb-6 lg:mb-8">
                Focus on <span className="text-gray-300">Creativity</span>, Not Administration
              </h2>
              <p className="text-lg lg:text-xl leading-relaxed text-gray-300 mb-8 lg:mb-10">
                Stop losing time on paperwork and start growing your creative business. 
                ShootSuite automates the boring stuff so you can focus on what you love.
              </p>
              <Link to="/auth" className="inline-block">
                <motion.button 
                  className="btn-primary w-full sm:w-auto"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Start Your Journey
                </motion.button>
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="mt-16 lg:mt-0"
            >
              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit.text}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center space-x-4 glass-badge rounded-2xl p-6 hover:bg-white/20 transition-all duration-300"
                    whileHover={{ x: 5 }}
                  >
                    <benefit.icon className="w-6 h-6 text-gray-300 flex-shrink-0" />
                    <span className="text-white font-medium">{benefit.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-white mb-6 lg:mb-8">
              Ready to Transform Your <span className="text-gradient">Workflow</span>?
            </h2>
            <p className="text-lg lg:text-xl leading-relaxed text-gray-400 mb-8 lg:mb-12">
              Join thousands of creative professionals who've already streamlined their business with ShootSuite.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 lg:gap-6 justify-center">
              <Link to="/auth" className="w-full sm:w-auto">
                <motion.button 
                  className="btn-primary text-base lg:text-lg w-full py-4 px-8"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Get Started
                </motion.button>
              </Link>
              <motion.button 
                className="btn-secondary text-base lg:text-lg w-full sm:w-auto py-4 px-8"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Schedule Demo
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="glass-footer py-12 lg:py-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-2 lg:space-x-3">
              <div className="w-10 h-10 lg:w-12 lg:h-12 glass-badge rounded-2xl flex items-center justify-center">
                <CameraIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
              </div>
              <span className="text-xl lg:text-2xl font-bold text-white">
                Shoot<span className="text-gray-400">Suite</span>
              </span>
            </div>
            
            {/* Currency Selector and Copyright */}
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
              <div className="text-gray-400 text-sm font-medium text-center md:text-left">
                © 2025 ShootSuite. Built with ❤️ by <a href="https://www.instagram.com/evanball.jpg/" className="text-white hover:text-gray-300">evanball.jpg</a> for creative professionals.
              </div>
              <div className="flex items-center space-x-3">
                <motion.a
                  href="http://instagram.com/shootsuiteapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg 
                    className="w-4 h-4" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  <span>@shootsuiteapp</span>
                </motion.a>
                <CurrencySelector />
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage 